// FeedDetailDialog — Tier 2 drill-down for a single feed assignment.
//
// Fetches POST /api/windows/{id}/roster/feed-detail on open, renders:
//   - Summary stats (peak, percentiles, avg, duration)
//   - Time-series chart (peak line + avg line) with threshold reference lines
//     at 70 / 80 / 90 / 100 % of feed_rating_a
//   - Threshold-crossing summary ("above 80% for X sec")
//   - Load distribution histogram (vertical bars, CSS-only)
//
// Data source: cloud-victoria-metrics :10s_max stream-aggregated series.
// For HouseRight LX over AG-Oakland this matches edge VM 200ms native
// peak exactly (255.1A vs 255.1A) and reveals that 91% of the show ran
// under 78A despite the brief 255A spike — the kind of insight operator
// needs to size a feed correctly.

import { useEffect, useMemo, useRef, useState } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { getFeedDetail, ApiError } from "@/lib/api";
import type {
  FeedDetailResponse, FeedDetailHistogramBucket,
} from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatVenueLocalInline, partsInTz } from "@/lib/venueTime";

export interface FeedDetailDialogProps {
  open: boolean;
  onClose: () => void;
  windowId: string;
  periodId?: string;
  membershipIds: string[];
  feedRatingA: number;
  feedName: string;
  serviceNames: string[];
  /** IANA timezone for the target venue (e.g. "America/Los_Angeles").
   *  Drives the chart x-axis labels + periods_included timestamps. When
   *  null, the dialog falls back to UTC and tags timestamps with "(UTC)"
   *  so the missing venue tz is visible. */
  venueTz?: string | null;
}

function fmtA(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${v.toFixed(0)}A`;
}
function fmtMin(s: number): string {
  if (s < 60) return `${s}s`;
  const m = s / 60;
  return m < 10 ? `${m.toFixed(1)} min` : `${m.toFixed(0)} min`;
}

export function FeedDetailDialog({
  open, onClose, windowId, periodId, membershipIds, feedRatingA,
  feedName, serviceNames, venueTz,
}: FeedDetailDialogProps) {
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "ready"; data: FeedDetailResponse }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  useEffect(() => {
    if (!open) {
      setState({ kind: "idle" });
      return;
    }
    setState({ kind: "loading" });
    const ctrl = new AbortController();
    getFeedDetail(
      windowId,
      {
        membership_ids: membershipIds,
        feed_rating_a: feedRatingA,
        ...(periodId ? { period_id: periodId } : {}),
      },
      { signal: ctrl.signal },
    )
      .then((d) => setState({ kind: "ready", data: d }))
      .catch((e: Error) => {
        if (e.name === "AbortError") return;
        const msg = e instanceof ApiError
          ? `${e.status} ${e.message}`
          : e.message;
        setState({ kind: "error", message: msg });
      });
    return () => ctrl.abort();
  }, [open, windowId, periodId, membershipIds.join(","), feedRatingA]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{feedName}</DialogTitle>
          <div className="text-xs text-muted-foreground">
            {serviceNames.join(", ")} · breaker {feedRatingA}A
          </div>
        </DialogHeader>

        {state.kind === "loading" && (
          <p className="text-sm text-muted-foreground py-8 text-center">
            Loading drill-down…
          </p>
        )}
        {state.kind === "error" && (
          <p className="text-sm text-red-400 py-4">
            Could not load detail: {state.message}
          </p>
        )}
        {state.kind === "ready" && (
          <FeedDetailBody data={state.data} feedRatingA={feedRatingA} venueTz={venueTz ?? null} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function FeedDetailBody({
  data, feedRatingA, venueTz,
}: {
  data: FeedDetailResponse;
  feedRatingA: number;
  venueTz: string | null;
}) {
  const s = data.summary;
  if (!s || data.source === "unavailable") {
    return (
      <p className="text-sm text-muted-foreground py-4">
        No telemetry available for this assignment over the show period.
      </p>
    );
  }
  return (
    <div className="space-y-4 text-sm">
      {/* Summary stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
        <Stat label="Peak (10s)" value={fmtA(s.peak_a)} tone={
          (s.peak_a ?? 0) > feedRatingA ? "red"
          : (s.peak_a ?? 0) > feedRatingA * 0.9 ? "amber"
          : "green"
        } />
        <Stat label="p99" value={fmtA(s.p99)} />
        <Stat label="p95" value={fmtA(s.p95)} />
        <Stat label="Median" value={fmtA(s.p50)} />
        <Stat label="Average" value={fmtA(s.avg)} />
      </div>

      {/* Time series chart */}
      <FeedTimeSeriesChart data={data} feedRatingA={feedRatingA} venueTz={venueTz} />

      {/* Threshold crossings */}
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
          Threshold crossings (cumulative time above each rating threshold)
        </div>
        <table className="text-xs w-full">
          <tbody>
            {Object.entries(data.threshold_crossings)
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([pct, t]) => {
                const tone = t.seconds_above > 0
                  ? Number(pct) >= 100 ? "text-red-400"
                  : Number(pct) >= 90 ? "text-amber-400"
                  : Number(pct) >= 80 ? "text-amber-300"
                  : "text-emerald-400"
                  : "text-muted-foreground";
                return (
                  <tr key={pct} className="border-b border-border/30 last:border-0">
                    <td className="py-1 w-16">{pct}%</td>
                    <td className="py-1 text-muted-foreground w-20">
                      ≥{fmtA(t.threshold_a)}
                    </td>
                    <td className={cn("py-1", tone)}>
                      {fmtMin(t.seconds_above)}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Histogram */}
      <FeedHistogram histogram={data.histogram} feedRatingA={feedRatingA} />

      {/* Periods that contributed to the lifetime aggregate (only when the
          backend ran in iterate-all-completed-periods mode). The flagged
          chart_period is the show whose time series is rendered above —
          where the worst combined bucket happened. */}
      {data.periods_included && data.periods_included.length > 0 && (
        <div className="text-[11px] pt-2 border-t border-border/30 space-y-1">
          <div className="uppercase tracking-wide text-muted-foreground">
            Show periods included ({data.periods_included.length})
          </div>
          <ul className="space-y-0.5">
            {data.periods_included.map((p) => (
              <li key={p.period_id} className="flex items-center justify-between gap-2">
                <span className={cn(
                  "tabular-nums",
                  p.is_chart_period ? "text-emerald-300" : "text-muted-foreground",
                )}>
                  {formatVenueLocalInline(p.started_at_ns / 1_000_000, venueTz)}
                  {" → "}
                  {formatVenueLocalInline(p.ended_at_ns / 1_000_000, venueTz, { include_date: false })}
                </span>
                <span className={cn(
                  "tabular-nums",
                  p.is_chart_period ? "text-emerald-300 font-medium" : "text-muted-foreground",
                )}>
                  {fmtA(p.peak_a)}{p.is_chart_period && " ◀ chart"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-[10px] text-muted-foreground pt-2 border-t border-border/30">
        Source: cloud :10s_max series · {s.duration_s}s
        {data.source === "cloud_vm_10s_max_lifetime"
          ? " total across periods · lifetime peak (matches show_condition_max_a)"
          : " show window"}
        {" · "}peak shown per worst leg (sum across services, max across phases)
        {!venueTz && (
          <span className="ml-1 text-amber-400">
            · times in UTC (venue tz unset; set on /admin/venues)
          </span>
        )}
      </div>
    </div>
  );
}

function Stat({
  label, value, tone,
}: {
  label: string;
  value: string;
  tone?: "green" | "amber" | "red";
}) {
  const toneClass = tone === "red" ? "text-red-400"
    : tone === "amber" ? "text-amber-400"
    : tone === "green" ? "text-emerald-400"
    : "";
  return (
    <div className="rounded border border-border/40 px-2 py-1">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={cn("text-base font-medium", toneClass)}>{value}</div>
    </div>
  );
}

function FeedTimeSeriesChart({
  data, feedRatingA, venueTz,
}: {
  data: FeedDetailResponse;
  feedRatingA: number;
  venueTz: string | null;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const uRef = useRef<uPlot | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    const series = data.time_series;
    if (series.length === 0) return;
    // Convert ns → epoch seconds (uPlot's default time scale).
    const xs = series.map((row) => row[0] / 1_000_000_000);
    const peaks = series.map((row) => row[1]);
    const avgs = series.map((row) => row[2] ?? null);
    const plot: uPlot.AlignedData = [xs, peaks, avgs];
    // X-axis tick labels: rendered in venue tz so the operator scans the
    // chart in the show's local wall-clock, not their browser's. Falls
    // back to UTC + "(UTC)" hint on the axis label when venueTz is missing.
    const tzForAxis = (venueTz && venueTz.trim()) ? venueTz : "UTC";

    const thresholds = [
      { y: feedRatingA * 0.7, color: "#34d399", label: "70%" },
      { y: feedRatingA * 0.8, color: "#fbbf24", label: "80%" },
      { y: feedRatingA * 0.9, color: "#fb923c", label: "90%" },
      { y: feedRatingA * 1.0, color: "#ef4444", label: "100%" },
    ];

    const thresholdPlugin: uPlot.Plugin = {
      hooks: {
        drawClear: (u) => {
          const ctx = u.ctx;
          ctx.save();
          ctx.setLineDash([4, 3]);
          ctx.lineWidth = 1;
          for (const t of thresholds) {
            const yPos = u.valToPos(t.y, "y", true);
            if (yPos < u.bbox.top || yPos > u.bbox.top + u.bbox.height) continue;
            ctx.strokeStyle = t.color;
            ctx.beginPath();
            ctx.moveTo(u.bbox.left, yPos);
            ctx.lineTo(u.bbox.left + u.bbox.width, yPos);
            ctx.stroke();
          }
          ctx.restore();
        },
      },
    };

    const opts: uPlot.Options = {
      width: ref.current.clientWidth,
      height: 260,
      scales: { x: { time: true } },
      axes: [
        {
          stroke: "#94a3b8",
          // Venue-tz tick labels — partsInTz formats each tick value's
          // ISO instant in the venue zone and returns HH:MM (or HH:MM
          // with the date on the first tick of a day).
          values: (_u, splits) => splits.map((tick_s, i) => {
            const iso = new Date(tick_s * 1000).toISOString();
            const p = partsInTz(iso, tzForAxis);
            // First tick or day-rollover tick → show date prefix.
            if (i === 0) return `${p.month}-${p.day} ${p.hour}:${p.minute}`;
            const prev = new Date(splits[i - 1] * 1000).toISOString();
            const prevP = partsInTz(prev, tzForAxis);
            if (prevP.day !== p.day) {
              return `${p.month}-${p.day} ${p.hour}:${p.minute}`;
            }
            return `${p.hour}:${p.minute}`;
          }),
          label: venueTz ? undefined : "time (UTC — venue tz unset)",
          labelSize: venueTz ? 0 : 14,
        },
        { stroke: "#94a3b8", label: "Amps", labelSize: 14 },
      ],
      series: [
        {},
        {
          label: "peak (10s)",
          stroke: "#f87171",
          width: 1.5,
          points: { show: false },
        },
        {
          label: "avg (10s)",
          stroke: "#60a5fa",
          width: 1,
          points: { show: false },
        },
      ],
      plugins: [thresholdPlugin],
      legend: { show: true },
    };

    if (uRef.current) {
      uRef.current.destroy();
      uRef.current = null;
    }
    uRef.current = new uPlot(opts, plot, ref.current);

    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        if (uRef.current) {
          uRef.current.setSize({
            width: e.contentRect.width,
            height: 260,
          });
        }
      }
    });
    ro.observe(ref.current);
    return () => {
      ro.disconnect();
      if (uRef.current) {
        uRef.current.destroy();
        uRef.current = null;
      }
    };
  }, [data, feedRatingA, venueTz]);

  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
        Load over show window
        <span className="ml-2 text-[10px] normal-case text-muted-foreground/70">
          (peak in red, avg in blue; dashed lines at 70 / 80 / 90 / 100% of breaker)
        </span>
      </div>
      <div ref={ref} style={{ width: "100%", height: 260 }} />
    </div>
  );
}

function FeedHistogram({
  histogram, feedRatingA,
}: {
  histogram: FeedDetailHistogramBucket[];
  feedRatingA: number;
}) {
  const maxCount = useMemo(
    () => Math.max(1, ...histogram.map((b) => b.count)),
    [histogram],
  );
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
        Load distribution
        <span className="ml-2 text-[10px] normal-case text-muted-foreground/70">
          (how many 10s samples in each amperage bucket)
        </span>
      </div>
      <div className="flex items-end gap-1 h-32 border-b border-border/40">
        {histogram.map((b, i) => {
          const mid = (b.min_a + b.max_a) / 2;
          const overBreaker = mid >= feedRatingA;
          const above80 = mid >= feedRatingA * 0.8;
          const tone = overBreaker ? "bg-red-500"
            : above80 ? "bg-amber-500"
            : "bg-emerald-500";
          const heightPct = (b.count / maxCount) * 100;
          return (
            <div key={i} className="flex-1 flex flex-col items-center justify-end">
              <div
                className={cn("w-full", tone, b.count === 0 && "opacity-20")}
                style={{ height: `${heightPct}%`, minHeight: b.count > 0 ? "2px" : "0" }}
                title={`${b.min_a.toFixed(0)}–${b.max_a.toFixed(0)}A: ${b.count} samples`}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
        <span>0A</span>
        <span>{histogram[Math.floor(histogram.length / 2)]?.max_a.toFixed(0)}A</span>
        <span>{histogram[histogram.length - 1]?.max_a.toFixed(0)}A</span>
      </div>
    </div>
  );
}
